import Dialog from "@/components/common/Dialog";
import { Button } from "@/components/ui/button";
import crossNew from "@/assets/svg/CrossIcon.svg";
import InputText from "@/components/common/InputText";
import Select, { SingleValue } from "react-select";
import { useTranslation } from "react-i18next";
import RedInfoIcon from "@/assets/svg/RedInfoIcon.svg";
import YallowInfoIcon from "@/assets/svg/YallowInfoIcon.svg";
import OrangeInfoIcon from "@/assets/svg/OrangeInfoIcon.svg";
import {
  ImpactEnumSchema,
  ProbabilityEnumSchema,
  SeverityEnumSchema,
  TrendEnumSchema,
  StatusEnumSchema,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import {
  createRiskSchema,
  updateRiskSchema,
} from "@backend/src/schemas/riskSchema";
import { useFormik } from "formik";
import z from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import ErrorMessage from "@/components/common/ErrorMessage";
import useAddRiskMemberMutation, {
  RiskType,
} from "@/api/mutation/useAddRIskMutation";
import { isAxiosError } from "axios";
import { toast } from "react-toastify";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import { errorConfig } from "@/lib/toastError";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import useTaskMemberListQuery from "@/api/query/useTaskMemberListQuary";
import UserAvatar from "@/components/ui/userAvatar";
import useUpdateRiskMemberMutation from "@/api/mutation/userUpdateRiskMutation";
import { useUser } from "@/hooks/useUser";
import { getSeverityAndColor } from "@/helperFuntions/riskSeverity";
import useProjectDetail from "@/api/query/useProjectDetailQuery";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UserOrganisationType } from "@/api/query/useOrganisationDetailsQuery";
type Options = { label: string; value: string };

const reactSelectStyle = {
  control: (
    provided: Record<string, unknown>,
    state: { isFocused: boolean }
  ) => ({
    ...provided,
    border: "1px solid #E7E7E7",
    paddingTop: "0.2rem",
    paddingBottom: "0.2rem",
    paddingLeft: "0.2rem",
    outline: state.isFocused ? "2px solid #299d91" : "0px solid #E7E7E7",
    boxShadow: state.isFocused ? "0px 0px 0px #299d91" : "none",
    "&:hover": {
      outline: state.isFocused ? "2px solid #299d91" : "0px solid #E7E7E7",
      boxShadow: "0px 0px 0px #299d91",
    },
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean }
  ) => ({
    ...provided,
    color: state.isSelected ? "white" : "black",
    background: state.isSelected ? "#299d91" : "white",
    "&:hover": {
      color: "white",
      background: "#299d9199",
    },
  }),
} as const;
type Option = {
  label: string;
  value: string;
};
function Riskform(props: {
  isOpen: boolean;
  setClose: (data: boolean, type: "close" | "update") => void;
  riskId: string;
  projectId: string;
  riskData?: RiskType;
  viewOnly?:boolean
}) {
  function enumToOptions(enumObj: any): Option[] {
    return Object.keys(enumObj).map((key) => ({
      label: t(enumObj[key as keyof typeof enumObj]),
      value: enumObj[key as keyof typeof enumObj],
    }));
  }
  const { t } = useTranslation();
  const riskCreateController = useAddRiskMemberMutation();
  const riskUpdateController = useUpdateRiskMemberMutation(props.riskId);
  const taskMemberList = useTaskMemberListQuery(props.projectId ?? "");
  const navigate = useNavigate();
  const labelStyle = "font-medium text-xs text-[#787486]/30 ";
  const projectDetailQuery = useProjectDetail(props.projectId ,false);
  const [disabled, setDisabled] = useState(props.viewOnly);
  const formik = useFormik<z.infer<typeof createRiskSchema>>({
    initialValues: {
      riskNameId: "",
      title: "",
      type: "",
      impact: "" as keyof typeof ImpactEnumSchema,
      probability: "" as keyof typeof ProbabilityEnumSchema,
      severity: "" as keyof typeof SeverityEnumSchema,
      trend: "" as keyof typeof TrendEnumSchema,
      projectId: props.projectId ?? "",
      consequences: "",
      correctiveMeasure: "",
      description: "",
      preventiveMeasure: "",
      responsibleUserId: null,
      status: "" as keyof typeof StatusEnumSchema,
    },
    validationSchema: toFormikValidationSchema(
      props.riskId ? updateRiskSchema : createRiskSchema
    ),
    onSubmit: (values, helper) => {
      if (!props.riskData) {
        riskCreateController.mutate(values, {
          onSuccess: (data) => {
            toast.success(t(data.data.message));
            props.setClose(false, "update");
          },
          onError: (error) => {
            if (isAxiosError(error)) {
              if (
                error.response?.status === 400 &&
                error.response.data?.errors &&
                Array.isArray(error.response?.data.errors)
              ) {
                error.response.data.errors.forEach((item) => {
                  helper.setFieldError(item.path[0], item.message);
                });
              }
              if (!Array.isArray(error.response?.data.errors)) {
                toast.error(
                  errorKeyFormate(error.response?.data.message + ""),
                  errorConfig
                );
              }
            }
          },
        });
      } else {
        riskUpdateController.mutate(values, {
          onSuccess: (data) => {
            toast.success(t(data.data.message));
            props.setClose(false, "update");
          },
          onError: (error) => {
            if (isAxiosError(error)) {
              if (
                error.response?.status === 400 &&
                error.response.data?.errors &&
                Array.isArray(error.response?.data.errors)
              ) {
                error.response.data.errors.forEach((item) => {
                  helper.setFieldError(item.path[0], item.message);
                });
              }
              if (!Array.isArray(error.response?.data.errors)) {
                toast.error(
                  errorKeyFormate(error.response?.data.message + ""),
                  errorConfig
                );
              }
            }
          },
        });
      }
    },
  });
  useEffect(() => {
    if (props.riskData) {
      formik.setValues(props.riskData);
      formik.setFieldValue("correctiveMeasure", props.riskData.correctiveMeasure ?? "" );

    }
  }, [props.riskData]);

  useEffect(() => {
    if (props.projectId ) {
      formik.setFieldValue("projectId", props.projectId );
    }
  }, [props.projectId]);


  
  useEffect(() => {
    if (props.viewOnly) {
      setDisabled(true);
    }else if (props.riskData && !props.viewOnly) {
      setDisabled(!allow(props.riskData));
    }
  }, [props.riskData, props.viewOnly]);
  
  const allow = (risk: RiskType) => {
    const riskResponsibleUser = risk.responsibleUser;
    const curruntUserProjectRole =
      projectDetailQuery.data?.data.data.assignedUsers.find(
        (item) => item.user.userId == user?.userId
      )?.projectRole;
    if (user?.userOrganisation[0].role == UserRoleEnumValue.ADMINISTRATOR) {
      return true;
    } else if (curruntUserProjectRole == UserRoleEnumValue.PROJECT_MANAGER) {
      return true;
    } else if (
      (riskResponsibleUser?.userId == user?.userId &&
        (curruntUserProjectRole == UserRoleEnumValue.TEAM_MEMBER || !curruntUserProjectRole ))
    ) {
      return true;
    } else {
      return false;
    }
  };

  const impactLevels = Object.keys(ImpactEnumSchema);
  const probabilityLevels = Object.keys(ProbabilityEnumSchema);

  useEffect(() => {
    if (formik.values.impact && formik.values.probability) {
      const severity = getSeverityAndColor(
        impactLevels.findIndex((t) => t == formik.values.impact),
        probabilityLevels.findIndex((t) => t == formik.values.probability),
        user?.userOrganisation[0].organisation?.severityTypes ?? []
      ).severity;
      formik.setFieldValue("severity", severity);
    }
  }, [formik.values]);
  const { user } = useUser();
  const [riskType, setRiskType] = useState<Options[]>();
  useEffect(() => {
    if (user && user.userOrganisation[0].organisation.riskType) {
      setRiskType(
        user.userOrganisation[0].organisation.riskType?.map((riskType) => {
          return { label: riskType, value: riskType };
        })
      );
    }
  }, [user]);
  const [projectMember, setprojectMember] = useState<UserOrganisationType[]>();
  useEffect(() => {
    setprojectMember(taskMemberList.data?.data.data);
  }, [taskMemberList.data?.data.data]);
  

  const getUser = () => {
    return (
      projectMember?.map((u) => {
        return {
          label: (
            <div className="flex gap-2 items-center">
              <UserAvatar user={u.user} />
              {u.user.firstName && u.user.lastName
                ? u.user.firstName + " " + u.user.lastName
                : u.user.email}
            </div>
          ) as unknown as string,
          value: u.user.userId,
        };
      }) ?? []
    );
  };

  return (
    <div>
      <Dialog
        isOpen={props.isOpen}
        onClose={() => {
          props.setClose(false, "close");
        }}
        modalClass="w-3/5 rounded-xl p-8 my-5 !text-left"
      >
        <div className="w-full h-full">
          <div className="text-4xl font-bold text-gray-450 flex justify-between items-center mb-4">
            {props.riskData && props.riskData?.riskId
              ? t("Update a Risk")
              : t("Add a new risk")}
            <Button
              variant={"none"}
              onClick={() => props.setClose(false, "close")}
              size={"icon"}
            >
              <img src={crossNew} className="w-4 min-w-4 brightness-0" />
            </Button>
          </div>
          <div className="w-full mb-3">
            <label className={labelStyle}>{t("Risk Title")}</label>
            <InputText
              disabled={disabled}
              placeholder={t("Risk Title")}
              className="mt-0"
              name="title"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.title}
            />
            {formik.touched.title && formik.errors.title && (
              <ErrorMessage className="text-xs">
                {formik.errors.title}
              </ErrorMessage>
            )}
          </div>
          <div className="flex items-start justify-between gap-3 w-full">
            <div className="w-1/2">
              <label className={labelStyle}>{t("Risk ID")}</label>
              <InputText
                disabled={disabled}
                placeholder={t("Risk ID")}
                className="mt-0"
                name="riskNameId"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.riskNameId}
              />
              {formik.touched.riskNameId && formik.errors.riskNameId && (
                <ErrorMessage className="text-xs">
                  {formik.errors.riskNameId}
                </ErrorMessage>
              )}
            </div>

            <div className="w-1/2">
              <label className={labelStyle}>{t("Risk Type")}</label>
              <Select
                isDisabled={disabled}
                key={formik.values.type}
                placeholder={t("Risk Type")}
                styles={reactSelectStyle}
                menuPlacement="auto"
                options={riskType}
                value={riskType?.find((t) => t.label === formik.values.type)}
                onChange={(e: SingleValue<Options>) =>
                  formik.setFieldValue("type", e?.value)
                }
                name="type"
                onBlur={formik.handleBlur}
              />
              {user &&
                user.userOrganisation[0].role &&
                user.userOrganisation[0].role ==
                  UserRoleEnumValue.ADMINISTRATOR && (
                <Link
                  className="text-primary-500 text-xs underline underline-offset-1"
                  to={`/organisation/${user?.userOrganisation[0].organisationId}`}
                  state={{ tab: 3 }}
                >
                  {t("Manage the list of types")}
                </Link>
              )}
              {formik.touched.type && formik.errors.type && (
                <ErrorMessage className="text-xs">
                  {formik.errors.type}
                </ErrorMessage>
              )}
            </div>
          </div>
          <div className="flex items-start justify-between gap-3 mt-2 w-full">
            <div className="w-1/2">
              <label className={labelStyle}>{t("Description")}</label>
              <textarea
                disabled={disabled}
                placeholder={t("Description")}
                cols={30}
                rows={4}
                className="w-full border rounded-md px-2 py-2 resize-none"
                name="description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
              />
              {formik.touched.description && formik.errors.description && (
                <ErrorMessage className="text-xs">
                  {t(formik.errors.description)}
                </ErrorMessage>
              )}
            </div>
            <div className="w-1/2">
              <label className={labelStyle}>{t("Consequences")}</label>
              <textarea
                disabled={disabled}
                placeholder={t("Consequences")}
                cols={30}
                rows={4}
                name="consequences"
                className="w-full border rounded-md px-2 py-2 resize-none"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.consequences}
              />
              {formik.touched.consequences && formik.errors.consequences && (
                <ErrorMessage className="text-xs">
                  {t(formik.errors.consequences)}
                </ErrorMessage>
              )}
            </div>
          </div>
          <div className="flex items-start justify-between gap-3 mt-2 w-full">
            <div className="w-1/2 flex justify-between gap-3">
              <div className="w-1/2">
                <label className={labelStyle}>{t("impact")}</label>
                <Select
                  isDisabled={disabled}
                  key={formik.values.impact}
                  placeholder={t("impact")}
                  styles={reactSelectStyle}
                  menuPlacement="auto"
                  options={enumToOptions(ImpactEnumSchema)}
                  defaultValue={enumToOptions(ImpactEnumSchema).find(
                    (t) => t.value == formik.values.impact
                  )}
                  name="impact"
                  onChange={(e: SingleValue<Options>) =>
                    formik.setFieldValue("impact", e?.value)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.impact && formik.errors.impact && (
                  <ErrorMessage className="text-xs">
                    {formik.errors.impact}
                  </ErrorMessage>
                )}
              </div>
              <div className="w-1/2">
                <label className={labelStyle}>{t("probability")}</label>
                <Select
                  isDisabled={disabled}
                  key={formik.values.probability}
                  placeholder={t("probability")}
                  styles={reactSelectStyle}
                  options={enumToOptions(ProbabilityEnumSchema)}
                  defaultValue={enumToOptions(ProbabilityEnumSchema).find(
                    (t) => t.value == formik.values.probability
                  )}
                  name="probability"
                  onChange={(e: SingleValue<Options>) =>
                    formik.setFieldValue("probability", e?.value)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.probability && formik.errors.probability && (
                  <ErrorMessage className="text-xs">
                    {formik.errors.probability}
                  </ErrorMessage>
                )}
              </div>
            </div>
            <div className="w-1/2 flex justify-between items-end gap-3">
              {formik.values.severity && (
                <div className="mb-2">
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`${
                            formik.values.severity ==
                            SeverityEnumSchema.VERY_CRITICAL
                              ? "text-[#EA4335]"
                              : formik.values.severity ==
                                SeverityEnumSchema.CRITICAL
                                ? "text-[#D98F00]"
                                : "text-[#FBD300]"
                          } text-lg text-nowrap font-semibold flex gap-1 w-1/2 items-center`}
                        >
                          <img
                            src={
                              formik.values.severity ==
                              SeverityEnumSchema.VERY_CRITICAL
                                ? RedInfoIcon
                                : formik.values.severity ==
                                  SeverityEnumSchema.CRITICAL
                                  ? OrangeInfoIcon
                                  : YallowInfoIcon
                            }
                            className="w-4.5 max-w-4.4 "
                          />
                          {t(formik.values.severity)}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="font-bold text-gray-500">
                          {t("severity")} = {t("impact")} * {t("probability")}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {user &&
                    user.userOrganisation[0].role &&
                    user.userOrganisation[0].role ==
                      UserRoleEnumValue.ADMINISTRATOR && (
                    <div
                      className="text-primary-500 text-xs underline underline-offset-1 cursor-pointer"
                      onClick={()=>{
                        navigate(`/organisation/${user?.userOrganisation[0].organisationId}`,{state:{tab:3}});
                      }}
                    >
                      {t("Manage the severity")}
                    </div>
                  )}
                </div>
              )}
              <div className="w-1/2">
                <label className={labelStyle}>{t("Status")}</label>
                <Select
                  isDisabled={disabled}
                  key={formik.values.status}
                  placeholder={t("Status")}
                  menuPlacement="auto"
                  styles={reactSelectStyle}
                  options={enumToOptions(StatusEnumSchema)}
                  defaultValue={enumToOptions(StatusEnumSchema).find(
                    (t) => t.value == formik.values.status
                  )}
                  name="status"
                  onChange={(e: SingleValue<Options>) =>
                    formik.setFieldValue("status", e?.value)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.status && formik.errors.status && (
                  <ErrorMessage className="text-xs">
                    {formik.errors.status}
                  </ErrorMessage>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-start justify-between gap-3 mt-2 w-full">
            <div className="w-1/2">
              <label className={labelStyle}>{t("Trend")}</label>
              <Select
                isDisabled={disabled}
                key={formik.values.trend}
                placeholder={t("Trend")}
                menuPlacement="auto"
                styles={reactSelectStyle}
                options={enumToOptions(TrendEnumSchema)}
                defaultValue={enumToOptions(TrendEnumSchema).find(
                  (t) => t.value == formik.values.trend
                )}
                name="trend"
                onChange={(e: SingleValue<Options>) =>
                  formik.setFieldValue("trend", e?.value)
                }
                onBlur={formik.handleBlur}
              />
              {formik.touched.trend && formik.errors.trend && (
                <ErrorMessage className="text-xs">
                  {formik.errors.trend}
                </ErrorMessage>
              )}
            </div>
            <div className="w-1/2">
              <label className={labelStyle}>{t("Responsible")}</label>
              <Select
                isDisabled={disabled}
                key={projectMember?.length}
                placeholder={t("Responsible")}
                styles={reactSelectStyle}
                menuPlacement="auto"
                options={getUser()}
                defaultValue={getUser().find(
                  (t) => t.value == formik.values.responsibleUserId
                )}
                onChange={(e: SingleValue<Options>) =>
                  formik.setFieldValue("responsibleUserId", e?.value)
                }
              />
            </div>
          </div>
          <div className="flex items-start justify-between gap-3 mt-2 w-full">
            <div className="w-1/2">
              <label className={labelStyle}>{t("Preventive Measures")}</label>
              <textarea
                disabled={disabled}
                placeholder={t("Preventive Measures")}
                cols={30}
                rows={4}
                className="w-full border rounded-md px-2 py-2 resize-none"
                name="preventiveMeasure"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.preventiveMeasure}
              />
              {formik.touched.preventiveMeasure &&
                formik.errors.preventiveMeasure && (
                <ErrorMessage className="text-xs">
                  {t(formik.errors.preventiveMeasure)}
                </ErrorMessage>
              )}
            </div>
            <div className="w-1/2">
              <label className={labelStyle}>{t("Corrective Measures")}</label>
              <textarea
                disabled={disabled}
                placeholder={t("Corrective Measures")}
                cols={30}
                rows={4}
                className="w-full border rounded-md px-2 py-2 resize-none"
                name="correctiveMeasure"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.correctiveMeasure}
              />
              {formik.touched.correctiveMeasure &&
                formik.errors.correctiveMeasure && (
                <ErrorMessage className="text-xs">
                  {t(formik.errors.correctiveMeasure)}
                </ErrorMessage>
              )}
            </div>
          </div>
          {!disabled && (
            <div className="flex justify-end">
              <Button
                size={"lg"}
                variant={"primary"}
                onClick={formik.submitForm}
              >
                {props.riskId ? t("update") : t("submit")}
              </Button>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

export default Riskform;
